<template>
  <div class="container-fluid search-page rfo-page rfo-page--standard oa-workspace">
    <div class="mb-3">
      <router-link
        :to="{ name: 'pay-operations-order-amendment-detail', params: { uuid: orderUuid } }"
        class="oa-back-link"
      >
        <i class="fa fa-arrow-left me-1"></i>Back to Order Detail
      </router-link>
    </div>

    <!-- Step indicator -->
    <div class="d-flex justify-content-center mb-4">
      <div class="d-flex align-items-center">
        <div v-for="(step, idx) in steps" :key="idx" class="d-flex align-items-center">
          <div
            class="rounded-circle d-flex align-items-center justify-content-center step-bubble"
            :class="idx <= currentStep ? 'bg-primary text-white' : 'bg-light text-muted'"
          >
            {{ idx + 1 }}
          </div>
          <span class="ms-2 me-3 small" :class="idx <= currentStep ? 'fw-bold' : 'text-muted'">{{
            step
          }}</span>
          <div v-if="idx < steps.length - 1" class="border-top mx-2 step-divider"></div>
        </div>
      </div>
    </div>

    <!-- Step 1: Receipt data -->
    <div
      v-if="currentStep === 0"
      class="card card-absolute rfo-card rfo-card--primary oa-section-card"
    >
      <div class="card-header bg-primary text-white">
        <h5 class="mb-0"><i class="fa fa-pencil me-2"></i>Receipt Data</h5>
      </div>
      <div class="card-body">
        <div class="row">
          <div class="col-md-6">
            <div class="mb-3">
              <label class="form-label fw-bold">Order UUID</label>
              <input
                :value="orderUuid"
                type="text"
                class="form-control border border-primary font-monospace"
                readonly
              />
            </div>
            <div class="mb-3">
              <label class="form-label fw-bold"
                >Reason <span class="text-danger">*</span> (min 10 chars)</label
              >
              <textarea
                v-model="reason"
                class="form-control border border-primary"
                rows="3"
                placeholder="Explain the drive-away scenario..."
              ></textarea>
              <div class="form-text">{{ reason.length }} / 10 minimum</div>
            </div>
          </div>
          <div class="col-md-6">
            <div v-if="caseDoc" class="border rounded p-3 mb-3 small">
              <h6 class="fw-bold">Order Summary</h6>
              <dl class="row mb-0">
                <dt class="col-sm-5">State</dt>
                <dd class="col-sm-7">{{ caseDoc.orderState }}</dd>
                <dt class="col-sm-5">Amount</dt>
                <dd class="col-sm-7">
                  {{ formatCurrency(caseDoc.totalAmount, caseDoc.currency) }}
                </dd>
                <dt class="col-sm-5">Partner</dt>
                <dd class="col-sm-7">{{ caseDoc.partnerName || '—' }}</dd>
              </dl>
            </div>
            <div
              v-else-if="caseError"
              class="alert alert-danger outline small d-flex align-items-center"
            >
              <span class="flex-grow-1">{{ caseError }}</span>
              <button class="btn btn-sm btn-outline-danger ms-2" @click="fetchCase">
                <i class="fa fa-refresh me-1"></i>Retry
              </button>
            </div>
            <div class="alert alert-warning outline small">
              <i class="fa fa-exclamation-triangle me-1"></i>
              Drive Away flow: complete the order first, then create the invoice with the selected
              payment object. Order mutation and invoice generation are separate outcomes.
            </div>
          </div>
        </div>
      </div>
      <div class="card-footer text-end">
        <button class="btn btn-primary" :disabled="reason.length < 10" @click="currentStep = 1">
          Next: Complete Order <i class="fa fa-arrow-right ms-1"></i>
        </button>
      </div>
    </div>

    <!-- Step 2: Complete the order -->
    <div
      v-if="currentStep === 1"
      class="card card-absolute rfo-card rfo-card--primary oa-section-card"
    >
      <div class="card-header bg-primary text-white">
        <h5 class="mb-0"><i class="fa fa-check me-2"></i>Complete Order</h5>
      </div>
      <div class="card-body">
        <p>Execute <strong>completeManually</strong> for this Drive Away order with reason:</p>
        <blockquote class="border-start border-3 ps-3 small">{{ reason }}</blockquote>
        <p class="small text-muted mb-0">
          A server-side preview is generated and consumed by the execute call — no draft can be
          substituted between preview and execute.
        </p>
      </div>
      <div class="card-footer d-flex justify-content-between">
        <button class="btn btn-outline-secondary" @click="currentStep = 0">
          <i class="fa fa-arrow-left me-1"></i>Back
        </button>
        <button class="btn btn-primary" :disabled="isCompleting" @click="completeOrder">
          <span v-if="isCompleting">
            <span class="spinner-border spinner-border-sm me-1"></span>Completing...
          </span>
          <span v-else><i class="fa fa-check me-1"></i>Complete Order</span>
        </button>
      </div>
    </div>

    <!-- Step 3: Payment object selection -->
    <div
      v-if="currentStep === 2"
      class="card card-absolute rfo-card rfo-card--primary oa-section-card"
    >
      <div class="card-header bg-primary text-white">
        <h5 class="mb-0"><i class="fa fa-credit-card me-2"></i>Invoice Payment Object</h5>
      </div>
      <div class="card-body">
        <div class="alert alert-info outline small mb-3">
          <i class="fa fa-info-circle me-1"></i>
          Select the payment object for the Odoo invoice queue message. The chosen payment object
          and your bank details are persisted for auditing.
        </div>

        <div class="mb-3">
          <label class="form-label fw-bold"
            >Payment Method <span class="text-danger">*</span></label
          >
          <div class="row g-2">
            <div v-for="(label, key) in DRIVE_AWAY_PAYMENT_LABELS" :key="key" class="col-md-4">
              <div
                class="border rounded p-3 text-center cursor-pointer payment-method-card"
                :class="selectedPaymentType === key ? 'border-primary bg-light' : ''"
                @click="selectPaymentType(key)"
              >
                <i class="fa fa-2x mb-1" :class="paymentIcon(key)"></i>
                <div class="small fw-bold">{{ label }}</div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="payment" class="border rounded p-3">
          <h6 class="fw-bold mb-3">Payment Details</h6>
          <div class="row g-2">
            <div class="col-md-6">
              <label class="form-label small mb-1">Bank Name</label>
              <input
                v-model="payment.bank_name"
                type="text"
                class="form-control border border-primary form-control-sm"
              />
            </div>
            <div class="col-md-6">
              <label class="form-label small mb-1">Masked IBAN</label>
              <input
                v-model="payment.masked_iban"
                type="text"
                class="form-control border border-primary form-control-sm"
              />
            </div>
            <div class="col-md-4">
              <label class="form-label small mb-1">Amount</label>
              <input
                v-model.number="payment.amount"
                type="number"
                step="0.01"
                class="form-control border border-primary form-control-sm"
              />
            </div>
            <div class="col-md-4">
              <label class="form-label small mb-1">Currency</label>
              <select
                v-model="payment.currency"
                class="form-select border border-primary form-select-sm"
              >
                <option v-for="code in currencies" :key="code" :value="code">{{ code }}</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label small mb-1">Date</label>
              <input
                v-model="payment.date"
                type="date"
                class="form-control border border-primary form-control-sm"
              />
            </div>
            <div class="col-md-6">
              <label class="form-label small mb-1">Payment Reference</label>
              <input
                v-model="payment.payment_reference"
                type="text"
                class="form-control border border-primary form-control-sm"
              />
            </div>
            <div class="col-md-6">
              <label class="form-label small mb-1">Payment Provider</label>
              <input
                v-model="payment.payment_provider"
                type="text"
                class="form-control border border-primary form-control-sm"
              />
            </div>
            <div class="col-12">
              <label class="form-label small mb-1">Note</label>
              <textarea
                v-model="payment.note"
                class="form-control border border-primary form-control-sm"
                rows="2"
              ></textarea>
            </div>
          </div>
        </div>
      </div>
      <div class="card-footer d-flex justify-content-between">
        <button class="btn btn-outline-secondary" @click="currentStep = 1">
          <i class="fa fa-arrow-left me-1"></i>Back
        </button>
        <button
          class="btn btn-success"
          :disabled="!payment || isCreatingInvoice"
          @click="createInvoice"
        >
          <span v-if="isCreatingInvoice">
            <span class="spinner-border spinner-border-sm me-1"></span>Creating...
          </span>
          <span v-else><i class="fa fa-file-text-o me-1"></i>Create Invoice</span>
        </button>
      </div>
    </div>

    <!-- Step 4: Result -->
    <div
      v-if="currentStep === 3"
      class="card card-absolute rfo-card rfo-card--primary oa-section-card"
    >
      <div
        class="card-header"
        :class="invoiceSuccess ? 'bg-success text-white' : 'bg-warning text-dark'"
      >
        <h5 class="mb-0">
          <i
            class="fa me-2"
            :class="invoiceSuccess ? 'fa-check-circle' : 'fa-exclamation-circle'"
          ></i>
          {{ invoiceSuccess ? 'Invoice Created' : 'Invoice Partial / Failed' }}
        </h5>
      </div>
      <div class="card-body">
        <div v-if="invoiceSuccess" class="alert alert-success outline small mb-0">
          Invoice generation was triggered successfully and the payment object has been persisted
          for auditing.
        </div>
        <div v-else>
          <div class="alert alert-warning outline small mb-2">
            <i class="fa fa-exclamation-triangle me-1"></i>
            The payment object was persisted, but invoice generation could not be triggered. This is
            a <strong>partial success</strong> — the order is already amended; the invoice needs
            manual follow-up.
          </div>
          <p v-if="invoiceError" class="small text-danger">{{ invoiceError }}</p>
          <div class="alert alert-warning outline small mb-0">
            <i class="fa fa-phone me-1"></i>
            Please escalate to the operations lead for manual invoice handling.
          </div>
        </div>
      </div>
      <div class="card-footer text-end">
        <router-link
          :to="{ name: 'pay-operations-order-amendment-detail', params: { uuid: orderUuid } }"
          class="btn btn-primary"
        >
          <i class="fa fa-arrow-left me-1"></i>Back to Order Detail
        </router-link>
      </div>
    </div>

    <PayOpsUsageInstructions
      class="mt-4"
      description="Guided flow for Drive Away cases: complete the order, then create the invoice with an operator-selected payment object that is persisted for auditing."
      :required-roles="driveAwayRequiredRoles"
      :workflow-notes="driveAwayWorkflowNotes"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useToast } from 'vue-toastification';
import { apiClient, API_PATHS } from '@/services/api';
import { formatCurrency } from '@/utils/display-formatters';
import {
  runPreview,
  runExecute,
} from '@/services/ryd-pay-operations/order-amendments/amendment-api';
import {
  DRIVE_AWAY_PAYMENT_DEFAULTS,
  DRIVE_AWAY_PAYMENT_LABELS,
} from '@/services/ryd-pay-operations/order-amendments/odoo-payment-constants';
import PayOpsUsageInstructions from '../shared/PayOpsUsageInstructions.vue';
import {
  PAY_OPS_DRIVE_AWAY_ROLES,
  buildDriveAwayWorkflowNotes,
} from '@/services/ryd-pay-operations/order-amendments/usage-instructions-config';
import { CURRENCIES } from '@/services/ryd-pay-operations/order-amendments/currency-constants';

const driveAwayRequiredRoles = PAY_OPS_DRIVE_AWAY_ROLES;
const driveAwayWorkflowNotes = buildDriveAwayWorkflowNotes();
const currencies = CURRENCIES;

const route = useRoute();
const toast = useToast();
const orderUuid = route.params.uuid;

const steps = ['Receipt Data', 'Complete Order', 'Payment Object', 'Result'];
const currentStep = ref(0);
const reason = ref('');
const caseDoc = ref(null);
const caseError = ref('');
const isCompleting = ref(false);
const selectedPaymentType = ref('');
const payment = ref(null);
const isCreatingInvoice = ref(false);
const invoiceSuccess = ref(false);
const invoiceError = ref('');

function paymentIcon(key) {
  switch (key) {
    case 'SEPA_BANK_TRANSFER':
      return 'fa-university';
    case 'PAYPAL':
      return 'fa-paypal';
    case 'CREDIT_CARD':
      return 'fa-credit-card';
    default:
      return 'fa-money';
  }
}

function selectPaymentType(key) {
  selectedPaymentType.value = key;
  payment.value = JSON.parse(JSON.stringify(DRIVE_AWAY_PAYMENT_DEFAULTS[key]));
  payment.value.date = new Date().toISOString().split('T')[0];
  if (caseDoc.value?.totalAmount) payment.value.amount = caseDoc.value.totalAmount;
}

async function fetchCase() {
  caseError.value = '';
  try {
    const { data } = await apiClient.rpIntegration.get(
      API_PATHS.rpIntegration.payOperations.orderAmendments.caseByUuid(orderUuid),
    );
    caseDoc.value = data?.case ?? data;
  } catch (err) {
    // Non-blocking for the flow itself, but the operator should know the
    // order summary is missing rather than assume the order has no data.
    caseError.value =
      err?.response?.data?.message || 'Could not load the order summary. The flow still works.';
  }
}

async function completeOrder() {
  isCompleting.value = true;
  try {
    const preview = await runPreview(orderUuid, {
      action: 'completeManually',
      draft: { captureOnAmend: false },
      reason: reason.value,
      reasonCategory: 'driveAway',
    });
    if (preview.blockers?.length) {
      toast.error(`Cannot complete: ${preview.blockers.join('; ')}`);
      return;
    }
    const result = await runExecute(orderUuid, {
      action: 'completeManually',
      previewToken: preview.previewToken,
      reasonCategory: 'driveAway',
    });
    if (result.success || result.partial) {
      toast.success('Order completed.');
      currentStep.value = 2;
    } else {
      toast.error(result.message ?? 'Failed to complete order.');
    }
  } catch (err) {
    toast.error(err?.response?.data?.message ?? 'Failed to complete order.');
  } finally {
    isCompleting.value = false;
  }
}

async function createInvoice() {
  if (!payment.value) return;
  isCreatingInvoice.value = true;
  invoiceError.value = '';
  try {
    const { data } = await apiClient.rpIntegration.post(
      API_PATHS.rpIntegration.payOperations.orderAmendments.driveAwayInvoice(orderUuid),
      { payment: payment.value },
    );
    invoiceSuccess.value = !!data?.invoiceTriggered;
    if (!invoiceSuccess.value && data?.invoiceError) {
      invoiceError.value = data.invoiceError;
    }
  } catch (err) {
    invoiceSuccess.value = false;
    invoiceError.value = err?.response?.data?.message ?? 'Invoice generation failed.';
  } finally {
    isCreatingInvoice.value = false;
    currentStep.value = 3;
  }
}

onMounted(fetchCase);
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.payment-method-card {
  transition: border-color 0.15s ease-in-out;
}
</style>
